import { useState, useEffect, useMemo } from "react";
import { Plus, Trash, MapPin, Warning, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import MapPicker from "@/components/MapPicker";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { VALUATION_SECTIONS, computeBuildingBlockRow } from "@/lib/valuationFields";

const NONE = "__none__";

const FORMAT_PRESETS = { open_land: ["A"], land_building: ["A", "B"], industrial_land: ["A", "B", "F"], flat: ["A", "B", "C", "D", "F"] };

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const sumRows = (rows, key) => (Array.isArray(rows) ? rows : []).reduce((s, r) => s + toNum(r[key]), 0);

// Indian-system number → words, e.g. 10276495 → "Rupees One Crore Two Lakh Seventy Six Thousand Four Hundred Ninety Five Only".
const _ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const _TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
const _two = (n) => (n < 20 ? _ONES[n] : _TENS[Math.floor(n / 10)] + (n % 10 ? " " + _ONES[n % 10] : ""));
const _three = (n) => {
  const h = Math.floor(n / 100), r = n % 100;
  return (h ? _ONES[h] + " Hundred" : "") + (r ? (h ? " " : "") + _two(r) : "");
};
export function numberToIndianWords(value) {
  let n = Math.round(toNum(value));
  if (!n || n <= 0) return "";
  const parts = [];
  const crore = Math.floor(n / 10000000); n %= 10000000;
  const lakh = Math.floor(n / 100000); n %= 100000;
  const thousand = Math.floor(n / 1000); n %= 1000;
  if (crore) parts.push(_three(crore) + " Crore");
  if (lakh) parts.push(_two(lakh) + " Lakh");
  if (thousand) parts.push(_two(thousand) + " Thousand");
  if (n) parts.push(_three(n));
  return "Rupees " + parts.join(" ") + " Only";
}

const median = (arr) => {
  const a = arr.slice().sort((x, y) => x - y);
  if (!a.length) return 0;
  const m = Math.floor(a.length / 2);
  return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
};

// All "value" fields auto-compute. FMV = Part A (land) + Part B (building).
export function withDerived(d) {
  const area = toNum(d.land_area_sqft) || toNum(d.total_land_area_sqft);
  const landRate = toNum(d.land_rate_per_sqft);
  const glRate = toNum(d.guideline_rate_per_sqft);
  const mktRate = toNum(d.prevailing_market_rate);
  const round = (n) => (n ? Math.round(n) : "");
  const adoptedRate = toNum(d.adopted_rate) || landRate || mktRate;
  const land_value = round(area * adoptedRate);
  const guideline_value = round(area * glRate);
  const estimated_land_value = round(area * adoptedRate);
  const partA = (toNum(estimated_land_value) || toNum(land_value)) + sumRows(d.extra_land_details, "amount");
  const building_blocks = (Array.isArray(d.building_blocks) ? d.building_blocks : []).map((r) => ({ ...r, ...computeBuildingBlockRow(r) }));
  const partB = sumRows(building_blocks, "amount");
  const partC = sumRows(d.part_c_items, "amount");
  const partD = sumRows(d.part_d_items, "amount");
  const partE = sumRows(d.part_e_items, "amount");
  const partF = sumRows(d.part_f_items, "amount");
  const pe = d.parts_enabled || {};
  const g = (L, v) => (pe[L] === false ? 0 : v);
  const total = g("A", partA) + g("B", partB) + g("C", partC) + g("D", partD) + g("E", partE) + g("F", partF);
  const fmv = total ? Math.round(total) : "";
  return {
    ...d,
    building_blocks,
    land_value,
    guideline_value,
    estimated_land_value,
    part_a_value: partA ? Math.round(partA) : "",
    part_b_value: partB ? Math.round(partB) : "",
    part_c_value: partC ? Math.round(partC) : "",
    part_d_value: partD ? Math.round(partD) : "",
    part_e_value: partE ? Math.round(partE) : "",
    part_f_value: partF ? Math.round(partF) : "",
    total_value: total ? Math.round(total) : "",
    fmv,
    realisable_value_90: fmv ? Math.round(fmv * 0.9) : "",
    distress_value_80: fmv ? Math.round(fmv * 0.8) : "",
    guideline_value_summary: guideline_value,
    market_value_words: numberToIndianWords(fmv),
  };
}

function Field({ field, value, onChange }) {
  const id = `vd-${field.key}`;
  if (field.type === "checkbox") {
    const checked = value === "Yes" || value === true;
    return (
      <div className="sm:col-span-2 flex items-start gap-2.5 rounded-md border border-border bg-muted/20 px-3 py-2">
        <Checkbox id={id} checked={checked} onCheckedChange={(c) => onChange(field.key, c ? "Yes" : "")} data-testid={id} className="mt-0.5" />
        <Label htmlFor={id} className="text-xs leading-snug cursor-pointer font-normal">{field.label}</Label>
      </div>
    );
  }
  if (field.computed) {
    const display = value !== "" && value != null ? `₹ ${Number(value).toLocaleString("en-IN")}` : "—";
    return (
      <div className={`space-y-1 ${field.full ? "sm:col-span-2" : ""}`}>
        <Label className="text-xs">{field.label}</Label>
        <Input readOnly value={display} className="h-9 bg-muted/60 text-primary font-semibold tabular" data-testid={id} />
        {field.hint && <p className="text-[10px] text-accent">{field.hint}</p>}
      </div>
    );
  }
  if (field.readOnly) {
    return (
      <div className={`space-y-1 ${field.full ? "sm:col-span-2" : ""}`}>
        <Label className="text-xs">{field.label}</Label>
        <Input readOnly value={value || ""} placeholder={field.placeholder} className="h-9 bg-muted/60 text-primary font-medium" data-testid={id} />
        {field.hint && <p className="text-[10px] text-muted-foreground">{field.hint}</p>}
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div className={`space-y-1.5 ${field.full ? "sm:col-span-2" : ""}`}>
        <Label className="text-xs">{field.label}</Label>
        <Textarea rows={2} value={value || ""} onChange={(e) => onChange(field.key, e.target.value)} data-testid={id} />
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <div className={`space-y-1.5 ${field.full ? "sm:col-span-2" : ""}`}>
        <Label className="text-xs">{field.label}</Label>
        <Select value={value || NONE} onValueChange={(v) => onChange(field.key, v === NONE ? "" : v)}>
          <SelectTrigger className="h-9 text-sm" data-testid={id}><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>—</SelectItem>
            {field.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }
  return (
    <div className={`space-y-1.5 ${field.full ? "sm:col-span-2" : ""}`}>
      <Label className="text-xs">{field.label}</Label>
      <Input type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
        value={value || ""} onChange={(e) => onChange(field.key, e.target.value)} className="h-9" data-testid={id} />
    </div>
  );
}

const PART_OF_SECTION = {
  land_valuation: "A", comparable_sales: "A",
  building_valuation: "B", building_blocks: "B", spec_items: "B",
  part_c_items: "C", part_d_items: "D", part_e_items: "E", part_f_items: "F",
};
const PART_CHOICES = [["A", "Part A · Land"], ["B", "Part B · Building"], ["C", "Part C · Extra Items"], ["D", "Part D · Amenities"], ["E", "Part E · Misc"], ["F", "Part F · Services"]];

function RowsEditor({ section, rows, setRows }) {
  const blank = Object.fromEntries(section.columns.map((c) => [c.key, ""]));
  const update = (i, k, v) => setRows(rows.map((r, idx) => {
    if (idx !== i) return r;
    const nr = { ...r, [k]: v };
    if (section.computeRow) {
      Object.assign(nr, section.computeRow(nr));
    } else if (section.computeValue) {
      const cv = section.computeValue;
      const a = toNum(nr[cv.areaKey]);
      const rt = toNum(nr[cv.rateKey]);
      nr[cv.valueKey] = a && rt ? Math.round(a * rt) : "";
    }
    return nr;
  }));
  const add = () => setRows([...rows, { ...blank }]);
  const remove = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const toggleExclude = (i) => setRows(rows.map((r, idx) => (idx === i ? { ...r, excluded: !r.excluded } : r)));
  const hasRate = section.columns.some((c) => c.key === "rate");
  const rateVals = hasRate ? rows.filter((r) => !r.excluded).map((r) => toNum(r.rate)).filter((x) => x > 0) : [];
  const rateMed = median(rateVals);
  const isOutlier = (r) => hasRate && !r.excluded && rateVals.length >= 3 && rateMed > 0 && toNum(r.rate) > 0 && Math.abs(toNum(r.rate) - rateMed) / rateMed > 0.4;
  return (
    <div className="space-y-2" data-testid={`vd-rows-${section.rowKey}`}>
      {rows.length === 0 && <p className="text-xs text-muted-foreground">No rows added yet.</p>}
      {rows.map((r, i) => (
        <div key={i} className={`rounded-md border p-2 ${r.excluded ? "border-slate-300 bg-slate-100" : isOutlier(r) ? "border-amber-400 bg-amber-50" : "border-border bg-muted/30"}`}>
          <div className="flex items-end gap-2">
            <div className={`grid grid-cols-2 sm:grid-cols-4 gap-2 flex-1 ${r.excluded ? "opacity-50" : ""}`}>
              {section.columns.map((c) => (
                <div key={c.key} className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">{c.label}</Label>
                  {c.computed ? (
                    <Input readOnly value={r[c.key] !== "" && r[c.key] != null ? `₹ ${Number(r[c.key]).toLocaleString("en-IN")}` : "—"} className="h-8 text-xs bg-muted/60 text-primary font-semibold" data-testid={`vd-${section.rowKey}-${i}-${c.key}`} />
                  ) : (
                    <Input value={r[c.key] || ""} onChange={(e) => update(i, c.key, e.target.value)} className={`h-8 text-xs ${c.key === "rate" && isOutlier(r) ? "border-amber-400 text-amber-800 font-semibold" : ""}`} data-testid={`vd-${section.rowKey}-${i}-${c.key}`} />
                  )}
                </div>
              ))}
            </div>
            <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive shrink-0" onClick={() => remove(i)} data-testid={`vd-${section.rowKey}-del-${i}`}><Trash size={15} /></Button>
          </div>
          {hasRate && toNum(r.rate) > 0 && (r.excluded || isOutlier(r)) && (
            <div className="mt-1.5 flex items-center justify-between gap-2">
              {r.excluded ? (
                <span className="text-[10px] text-slate-500 flex items-center gap-1" data-testid={`vd-${section.rowKey}-excluded-${i}`}><EyeSlash size={12} className="shrink-0" /> Excluded from the market average</span>
              ) : (
                <span className="text-[10px] text-amber-700 flex items-center gap-1" data-testid={`vd-${section.rowKey}-outlier-${i}`}><Warning size={12} weight="fill" className="shrink-0" /> Rate ₹{Number(toNum(r.rate)).toLocaleString("en-IN")} is {Math.round(Math.abs(toNum(r.rate) - rateMed) / rateMed * 100)}% off the median ₹{Number(rateMed).toLocaleString("en-IN")}/sq.ft</span>
              )}
              <Button type="button" size="sm" variant="ghost" className="h-6 text-[10px] shrink-0 rounded-full" onClick={() => toggleExclude(i)} data-testid={`vd-${section.rowKey}-${r.excluded ? "include" : "exclude"}-${i}`}>{r.excluded ? "Include" : "Exclude from average"}</Button>
            </div>
          )}
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" onClick={add} data-testid={`vd-${section.rowKey}-add`}><Plus size={13} className="mr-1" /> Add Row</Button>
    </div>
  );
}

function ItemsEditor({ section, rows, setRows }) {
  const vf = section.valueField;
  const list = Array.isArray(rows) ? rows : [];
  const map = Object.fromEntries(list.map((r) => [r.name, r[vf]]));
  const update = (name, val) => {
    const next = { ...map, [name]: val };
    const arr = section.items
      .filter((n) => next[n] !== "" && next[n] != null)
      .map((n) => ({ name: n, [vf]: next[n] }));
    setRows(arr);
  };
  const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  return (
    <div className="space-y-1.5" data-testid={`vd-items-${section.itemKey}`}>
      {section.items.map((name) => (
        <div key={name} className="flex items-center gap-2">
          <Label className="text-xs flex-1 text-muted-foreground">{name}</Label>
          <Input
            type={section.money ? "number" : "text"}
            value={map[name] ?? ""}
            onChange={(e) => update(name, e.target.value)}
            placeholder={section.money ? "₹ value" : "specification"}
            className="h-8 text-xs w-48"
            data-testid={`vd-${section.itemKey}-${slug(name)}`}
          />
        </div>
      ))}
    </div>
  );
}

export default function ValuationDetailsForm({ value, onChange, layout }) {
  const data = useMemo(() => withDerived(value || {}), [value]);
  const landOnly = layout === "open_land";
  const setField = (k, v) => onChange(withDerived({ ...data, [k]: v }));
  const addComparable = (c) => {
    const row = { property: c.property_address || c.reference || "", area: c.area || "", rate: c.rate || "",
                  date: (c.date || "").slice(0, 10), source: `Nearby case ${c.reference || ""} (${c.distance || ""})`.trim() };
    const existing = Array.isArray(data.comparable_sales) ? data.comparable_sales : [];
    onChange(withDerived({ ...data, comparable_sales: [...existing, row] }));
    toast.success("Added to Part A — Comparable Sale Evidence");
  };
  const setRows = (rowKey) => (rows) => onChange(withDerived({ ...data, [rowKey]: rows }));
  const [loadingGeo, setLoadingGeo] = useState(false);
  const [loadingRates, setLoadingRates] = useState(false);
  const [loadingLandmarks, setLoadingLandmarks] = useState(false);
  const [marketNote, setMarketNote] = useState("");
  const [nearbyCases, setNearbyCases] = useState([]);

  const plotLatLon = () => [parseFloat(data.latitude || data.field_lat), parseFloat(data.longitude || data.field_lon)];

  const marketTrend = useMemo(() => {
    const cs = Array.isArray(data.comparable_sales) ? data.comparable_sales : [];
    const rates = cs.filter((r) => !r.excluded).map((r) => toNum(r.rate)).filter((x) => x > 0);
    if (!rates.length) return null;
    const med = median(rates);
    const subject = toNum(data.prevailing_market_rate) || toNum(data.land_rate_per_sqft);
    const pct = subject && med ? Math.round(((subject - med) / med) * 100) : null;
    const excluded = cs.filter((r) => r.excluded && toNum(r.rate) > 0).length;
    return { median: med, lo: Math.min(...rates), hi: Math.max(...rates), count: rates.length, subject, pct, excluded };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.comparable_sales, data.prevailing_market_rate, data.land_rate_per_sqft]);

  // Auto-replicate Field Checked Latitude/Longitude into Property Location & Identifiers.
  useEffect(() => {
    const fl = data.field_lat;
    const fo = data.field_lon;
    const hasLat = fl !== undefined && fl !== null && String(fl).trim() !== "";
    const hasLon = fo !== undefined && fo !== null && String(fo).trim() !== "";
    const needLat = hasLat && String(fl) !== String(data.latitude ?? "");
    const needLon = hasLon && String(fo) !== String(data.longitude ?? "");
    if (needLat || needLon) {
      const patch = { ...data };
      if (needLat) patch.latitude = String(fl);
      if (needLon) patch.longitude = String(fo);
      onChange(withDerived(patch));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.field_lat, data.field_lon]);

  // Auto-apply valuation parts to match the selected report format whenever the format changes.
  useEffect(() => {
    const preset = FORMAT_PRESETS[layout];
    if (!preset || data.parts_preset_layout === layout) return;
    const newChoice = !data.parts_enabled || (data.parts_preset_layout && data.parts_preset_layout !== layout);
    if (newChoice) {
      onChange({ ...data, parts_preset_layout: layout, parts_enabled: Object.fromEntries(["A", "B", "C", "D", "E", "F"].map((L) => [L, preset.includes(L)])) });
    } else {
      // Existing job opened for the first time — record the format but keep its saved parts.
      onChange({ ...data, parts_preset_layout: layout });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout]);

  const fetchMarketRates = async () => {
    const [lat, lon] = plotLatLon();
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      toast.error("Set the plot Latitude & Longitude (Property Location or Field-checked) first");
      return;
    }
    setLoadingRates(true);
    try {
      const { data: res } = await api.post("/geo/market-rates", { lat, lon });
      const newRows = res.rows || [];
      if (!newRows.length) { setMarketNote(res.note || "No market rates found near these coordinates."); toast.info(res.note || "No market rates found near these coordinates"); return; }
      setMarketNote(res.note || "");
      const existing = Array.isArray(data.comparable_sales) ? data.comparable_sales : [];
      const seen = new Set(existing.map((r) => `${(r.property || "").toLowerCase()}|${r.rate}`));
      const merged = [...existing];
      newRows.forEach((r) => { const k = `${(r.property || "").toLowerCase()}|${r.rate}`; if (!seen.has(k)) { seen.add(k); merged.push(r); } });
      onChange(withDerived({ ...data, comparable_sales: merged }));
      toast.success(`Added ${merged.length - existing.length} ${res.fallback ? "firm-record comparable" : "market rate"}(s) to Part A`);
    } catch (e) {
      toast.error("Could not fetch market rates. Please try again.");
    } finally {
      setLoadingRates(false);
    }
  };

  const fetchLandmarks = async () => {
    const [lat, lon] = plotLatLon();
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      toast.error("Set the plot Latitude & Longitude (Property Location or Field-checked) first");
      return;
    }
    setLoadingLandmarks(true);
    try {
      const { data: res } = await api.post("/geo/nearby-landmarks", { lat, lon });
      const landmarks = res.landmarks || [];
      if (!landmarks.length) { toast.info(res.error || "No landmarks found near these coordinates"); return; }
      onChange(withDerived({ ...data, locational_intelligence: landmarks }));
      toast.success(`Auto-filled ${landmarks.length} landmark(s) within 3 km`);
    } catch (e) {
      toast.error("Could not fetch landmarks. Please try again.");
    } finally {
      setLoadingLandmarks(false);
    }
  };
  const runFieldScan = async () => {
    const lat = parseFloat(data.field_lat);
    const lon = parseFloat(data.field_lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      toast.error("Enter Field Checked Latitude & Longitude first");
      return;
    }
    setLoadingGeo(true);
    try {
      const res = await api.post("/geo/field-scan", { lat, lon });
      const landmarks = res.data.landmarks || [];
      const cases = res.data.cases || [];
      setNearbyCases(cases);
      onChange(withDerived({ ...data, latitude: String(lat), longitude: String(lon), locational_intelligence: landmarks }));
      if (res.data.error) toast.error(res.data.error);
      else toast.success(`Found ${landmarks.length} landmark(s) & ${cases.length} completed case(s) within 3 km`);
    } catch (e) {
      toast.error("Could not scan nearby data. Please try again.");
    } finally {
      setLoadingGeo(false);
    }
  };

  const pickCoords = (la, lo) => onChange(withDerived({ ...data, field_lat: la.toFixed(6), field_lon: lo.toFixed(6) }));
  const partOn = (L) => {
    const pe = data.parts_enabled || {};
    if (pe[L] === false) return false;
    if (pe[L] === true) return true;
    if (landOnly && ["B", "C", "D", "E"].includes(L)) return false;
    return true;
  };
  const togglePart = (L) => onChange({ ...data, parts_enabled: { ...(data.parts_enabled || {}), [L]: !partOn(L) } });
  const applyPreset = (keep) => onChange({ ...data, parts_enabled: Object.fromEntries(["A", "B", "C", "D", "E", "F"].map((L) => [L, keep.includes(L)])) });

  return (
    <div data-testid="valuation-details-form">
      <p className="text-xs text-muted-foreground mb-3">All fields optional — fill what's available. Saved with the job for reports & export.</p>
      <div className="mb-3 rounded-lg border border-border bg-muted/30 p-3" data-testid="parts-selector">
        <div className="text-xs font-semibold text-primary mb-2">Valuation parts to include <span className="font-normal text-muted-foreground">— switch off the parts you don't need for this property</span></div>
        {landOnly && <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 mb-2" data-testid="open-land-parts-hint">Open Land format: Building, Extra Items, Amenities &amp; Miscellaneous are hidden by default. Switch any part on if you need it.</p>}
        <div className="flex flex-wrap gap-2">
          {PART_CHOICES.map(([L, lbl]) => (
            <button key={L} type="button" onClick={() => togglePart(L)} data-testid={`part-toggle-${L}`} className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${partOn(L) ? "bg-primary text-white border-primary" : "bg-transparent text-muted-foreground border-border hover:border-primary/50"}`}>{partOn(L) ? "✓ " : ""}{lbl}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-border/60">
          <span className="text-[11px] text-muted-foreground">Presets:</span>
          <button type="button" onClick={() => applyPreset(["A"])} data-testid="preset-land-only" className="px-2.5 py-1 rounded-full text-[11px] font-semibold border border-border hover:border-primary/50">Land only</button>
          <button type="button" onClick={() => applyPreset(["A", "B"])} data-testid="preset-land-building" className="px-2.5 py-1 rounded-full text-[11px] font-semibold border border-border hover:border-primary/50">Land + Building</button>
          <button type="button" onClick={() => applyPreset(["A", "B", "C", "D", "E", "F"])} data-testid="preset-full" className="px-2.5 py-1 rounded-full text-[11px] font-semibold border border-border hover:border-primary/50">All parts</button>
          {FORMAT_PRESETS[layout] && (
            <button type="button" onClick={() => applyPreset(FORMAT_PRESETS[layout])} data-testid="preset-match-format" className="px-2.5 py-1 rounded-full text-[11px] font-semibold border border-accent/60 text-accent hover:bg-accent/10">Match format</button>
          )}
          <label className="ml-auto flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer" data-testid="hide-empty-parts-toggle">
            <input type="checkbox" checked={!!data.hide_empty_parts} onChange={(e) => onChange({ ...data, hide_empty_parts: e.target.checked })} className="accent-[#0A2540]" />
            Skip empty parts in the final report
          </label>
        </div>
      </div>
      <Accordion type="multiple" defaultValue={["owner_general"]} className="w-full">
        {VALUATION_SECTIONS.filter((section) => { const L = PART_OF_SECTION[section.id]; return !L || partOn(L); }).map((section) => (
          <AccordionItem key={section.id} value={section.id}>
            <AccordionTrigger className="text-sm font-semibold text-primary" data-testid={`vd-section-${section.id}`}>{section.title}</AccordionTrigger>
            <AccordionContent>
              {section.itemGroup ? (
                <ItemsEditor section={section} rows={Array.isArray(data[section.itemKey]) ? data[section.itemKey] : []} setRows={setRows(section.itemKey)} />
              ) : section.rows ? (
                <div className="space-y-2">
                  {section.id === "comparable_sales" && (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" disabled={loadingRates} onClick={fetchMarketRates} data-testid="fetch-market-rates-btn">
                        <MapPin size={13} className="mr-1" /> {loadingRates ? "Fetching market rates…" : "Fetch Market Rates for Lat/Long"}
                      </Button>
                      <span className="text-[10px] text-muted-foreground">Pulls live ₹/sq.ft from 99acres & MagicBricks near the plot coordinates.</span>
                      {marketNote && <p className="w-full text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5" data-testid="market-rates-note">{marketNote}</p>}
                      {marketTrend && (
                        <div className="w-full text-[11px] rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5" data-testid="market-rate-trend">
                          <span className="font-semibold text-primary">Indicative median market rate: ₹{marketTrend.median.toLocaleString("en-IN")}/sq.ft</span>
                          <span className="text-muted-foreground"> across {marketTrend.count} comparable{marketTrend.count > 1 ? "s" : ""} (range ₹{marketTrend.lo.toLocaleString("en-IN")}–₹{marketTrend.hi.toLocaleString("en-IN")}/sq.ft){marketTrend.excluded > 0 ? ` · ${marketTrend.excluded} outlier${marketTrend.excluded > 1 ? "s" : ""} excluded` : ""}</span>
                          {marketTrend.subject > 0 && marketTrend.pct !== null && (
                            <div className={`mt-0.5 font-medium ${marketTrend.pct > 0 ? "text-amber-700" : marketTrend.pct < 0 ? "text-emerald-700" : "text-slate-600"}`} data-testid="market-rate-trend-flag">
                              Your adopted rate ₹{marketTrend.subject.toLocaleString("en-IN")}/sq.ft is {Math.abs(marketTrend.pct)}% {marketTrend.pct > 0 ? "above" : marketTrend.pct < 0 ? "below" : "in line with"} the nearby median.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {section.id === "locational_intelligence" && (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" disabled={loadingLandmarks} onClick={fetchLandmarks} data-testid="fetch-landmarks-btn">
                        <MapPin size={13} className="mr-1" /> {loadingLandmarks ? "Fetching landmarks…" : "Auto-fill from Lat/Long"}
                      </Button>
                      <span className="text-[10px] text-muted-foreground">Scans schools, hospitals, roads & more within 3 km of the plot coordinates.</span>
                    </div>
                  )}
                  <RowsEditor section={section} rows={Array.isArray(data[section.rowKey]) ? data[section.rowKey] : []} setRows={setRows(section.rowKey)} />
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  {section.declarations && (
                    <div className="sm:col-span-2 flex items-center justify-between">
                      <p className="text-[11px] text-muted-foreground">Tick each declaration the valuer confirms — these print in Annexure-I (Declaration-cum-Undertaking) of the report.</p>
                      <Button type="button" size="sm" variant="outline" className="rounded-full text-xs h-7" data-testid="vd-confirm-all-decl"
                        onClick={() => { const upd = { ...data }; for (let i = 1; i <= 10; i++) upd[`decl_${i}`] = "Yes"; onChange(withDerived(upd)); }}>
                        Confirm all
                      </Button>
                    </div>
                  )}
                  {section.fields.map((f) => (
                    <Field key={f.key} field={f} value={data[f.key]} onChange={setField} />
                  ))}
                  {section.fieldScan && (
                    <div className="sm:col-span-2 rounded-md border border-border bg-muted/30 p-3 space-y-2">
                      <p className="text-[10px] text-muted-foreground">Click or drag the pin on the map to set the field-checked coordinates.</p>
                      <MapPicker lat={parseFloat(data.field_lat)} lon={parseFloat(data.field_lon)} onPick={pickCoords} />
                      <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" disabled={loadingGeo} onClick={runFieldScan} data-testid="vd-field-scan">
                        <MapPin size={13} className="mr-1" /> {loadingGeo ? "Scanning within 3 km…" : "Scan 3 km of Field-Checked Coordinates"}
                      </Button>
                      <p className="text-[10px] text-muted-foreground">Fills Locational Intelligence and lists already-completed cases within 3 km of the field-checked coordinates.</p>
                      {nearbyCases.length > 0 && (
                        <div className="space-y-1.5" data-testid="nearby-cases">
                          <p className="text-xs font-semibold text-primary">Completed cases within 3 km ({nearbyCases.length})</p>
                          {nearbyCases.map((c) => (
                            <div key={c.id} role="button" tabIndex={0} onClick={() => window.open(`/app/jobs?job=${c.id}`, "_blank")} onKeyDown={(e) => { if (e.key === "Enter") window.open(`/app/jobs?job=${c.id}`, "_blank"); }} className="rounded border border-border bg-background p-2 text-xs cursor-pointer hover:border-accent hover:bg-accent/5 transition-colors" title="Open this case's full details in a new tab" data-testid={`nearby-case-${c.id}`}>
                              <div className="flex justify-between gap-2">
                                <span className="font-semibold text-primary">{c.applicant_name || c.reference}</span>
                                <span className="text-accent font-mono">{c.distance}</span>
                              </div>
                              <div className="text-muted-foreground">{c.reference}{c.bank_name ? ` · ${c.bank_name}` : ""}</div>
                              {c.property_address && <div className="text-muted-foreground">{c.property_address}</div>}
                              {c.fmv ? <div className="text-foreground">FMV: ₹ {Number(c.fmv).toLocaleString("en-IN")}</div> : null}
                              {Array.isArray(c.locational_intelligence) && c.locational_intelligence.length > 0 && (
                                <div className="mt-1 text-[10px] text-muted-foreground">Landmarks: {c.locational_intelligence.map((l) => `${l.nearest_landmark} (${l.distance})`).join(", ")}</div>
                              )}
                              <Button type="button" size="sm" variant="outline" className="h-6 text-[10px] mt-1.5 rounded-full" onClick={(e) => { e.stopPropagation(); addComparable(c); }} data-testid={`use-comparable-${c.id}`}>Use as comparable</Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
